import { useState } from "react";

// ─── Pricing ─────────────────────────────────────────────────────────────────
const PRICES = { 30: 25, 60: 45, 120: 80 };
const DURATION_LABELS = { 30: "30 min", 60: "1 hour", 120: "2 hours" };

// ─── Schedule ─────────────────────────────────────────────────────────────────
const OPEN_HOUR = 8;
const CLOSE_HOUR = 22;
const HOURS = Array.from({ length: CLOSE_HOUR - OPEN_HOUR }, (_, i) => i + OPEN_HOUR);
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const FULL_DAYS = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];

// ─── Brand colours (matched to dextersportsyfc.org.uk) ───────────────────────
const C = {
  bg:        "#0a0f1e",
  header:    "#0d1f3c",
  surface:   "#0d1424",
  surface2:  "#111d30",
  border:    "#1e2f4a",
  gold:      "#c8102e",   // RED — primary CTA
  goldHover: "#a00d24",
  goldDark:  "#2a0a10",
  green:     "#15803d",
  greenBg:   "#052e16",
  greenBdr:  "#166534",
  blue:      "#0d1f3c",
  blueBg:    "#0a1428",
  blueBdr:   "#1e3a6b",
  red:       "#c8102e",
  redBg:     "#2a0a10",
  text:      "#f0f4f8",
  muted:     "#8b9ab5",
  faint:     "#1e2f4a",
};

// ─── Sample seed data ─────────────────────────────────────────────────────────
const SEED_BLOCKS = {
  "Mon-09": { label: "Dexters U16",    dur: 60,  recurring: true  },
  "Mon-11": { label: "Dexters U18",    dur: 60,  recurring: true  },
  "Wed-10": { label: "Dexters Seniors",dur: 60,  recurring: true  },
  "Wed-14": { label: "Dexters U14",    dur: 60,  recurring: true  },
  "Fri-09": { label: "Dexters Ladies", dur: 60,  recurring: true  },
  "Sat-10": { label: "Dexters U12",    dur: 120, recurring: true  },
  "Sat-14": { label: "Dexters U10",    dur: 60,  recurring: true  },
};
const SEED_BOOKINGS = {
  "Tue-13": { type: "external", name: "James O'Brien",  email: "james@email.com",  dur: 60,  paid: true,  method: "bank" },
  "Thu-15": { type: "external", name: "Five-a-side FC", email: "info@5aside.com",  dur: 120, paid: true,  method: "bank" },
  "Sat-16": { type: "external", name: "Park Rangers",   email: "rangers@mail.com", dur: 60,  paid: false, method: "dd"   },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getWeekDates(offset = 0) {
  const now = new Date(), day = now.getDay();
  const mon = new Date(now);
  mon.setDate(now.getDate() - (day === 0 ? 6 : day - 1) + offset * 7);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(mon); d.setDate(mon.getDate() + i); return d;
  });
}
function sKey(di, h) { return `${DAYS[di]}-${h}`; }
function fmt(d) { return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }); }

// ─── Shared styles ────────────────────────────────────────────────────────────
const S = {
  inp: { width: "100%", background: "#07101e", border: `1px solid ${C.border}`, color: C.text, padding: "10px 13px", borderRadius: 7, fontSize: 14, fontFamily: "Inter, sans-serif" },
  goldBtn: { width: "100%", background: "#c8102e", border: "none", color: "#ffffff", fontFamily: "Oswald, sans-serif", fontWeight: 900, fontSize: 17, letterSpacing: 2, padding: "13px", borderRadius: 8, marginTop: 18, cursor: "pointer", transition: "background .15s" },
  ghostBtn: { width: "100%", background: "transparent", border: `1px solid ${C.border}`, color: C.muted, fontFamily: "Oswald, sans-serif", fontSize: 14, letterSpacing: 1, padding: "9px 16px", borderRadius: 8, cursor: "pointer", marginTop: 8 },
  label: { display: "block", fontSize: 11, fontWeight: 600, letterSpacing: 1, color: C.muted, marginBottom: 5, textTransform: "uppercase" },
  card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 26 },
};

// ─── Sub-components ───────────────────────────────────────────────────────────
function Field({ label, children, mt = 12 }) {
  return <div style={{ marginBottom: 12, marginTop: mt }}><label style={S.label}>{label}</label>{children}</div>;
}
function Toggle({ on, onClick }) {
  return (
    <div onClick={onClick} style={{ width: 46, height: 25, background: on ? "#c8102e" : C.faint, borderRadius: 13, cursor: "pointer", position: "relative", transition: "background .2s", flexShrink: 0 }}>
      <div style={{ position: "absolute", top: 3, left: on ? 22 : 3, width: 19, height: 19, background: "#fff", borderRadius: 10, transition: "left .18s", boxShadow: "0 1px 4px rgba(0,0,0,.4)" }} />
    </div>
  );
}
function Stat({ label, value, color }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 18px" }}>
      <div style={{ fontSize: 10, color: C.muted, letterSpacing: 1, fontFamily: "Oswald", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontFamily: "Oswald", fontWeight: 900, fontSize: 32, color, marginTop: 2 }}>{value}</div>
    </div>
  );
}
function Th({ children }) {
  return <th style={{ fontFamily: "Oswald", fontWeight: 700, fontSize: 12, letterSpacing: 1, color: C.gold, textAlign: "left", padding: "12px 16px", borderBottom: `1px solid ${C.border}`, textTransform: "uppercase" }}>{children}</th>;
}
function Td({ children, style = {} }) {
  return <td style={{ padding: "11px 16px", fontSize: 13, color: C.text, ...style }}>{children}</td>;
}
function DangerBtn({ children, onClick }) {
  return <button onClick={onClick} style={{ background: "#c8102e", border: "none", color: "#ffffff", fontFamily: "Oswald", fontSize: 12, letterSpacing: 1, padding: "5px 12px", borderRadius: 4, cursor: "pointer" }}>{children}</button>;
}
function SectionHead({ children }) {
  return <h2 style={{ fontFamily: "Oswald", fontWeight: 700, fontSize: 22, letterSpacing: 2, marginBottom: 18, color: C.text }}>{children}</h2>;
}
function InfoBanner({ icon, title, sub, color = C.blueBg, bdr = C.blueBdr }) {
  return (
    <div style={{ background: color, border: `1px solid ${bdr}`, borderRadius: 8, padding: "10px 14px", marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
      <span style={{ fontSize: 20 }}>{icon}</span>
      <div><div style={{ fontFamily: "Oswald", fontWeight: 700, fontSize: 14, letterSpacing: 1 }}>{title}</div><div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{sub}</div></div>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function App() {
  const [view, setView]         = useState("calendar"); // calendar | booking | contact | adminLogin | admin
  const [weekOff, setWeekOff]   = useState(0);
  const [blocked, setBlocked]   = useState(SEED_BLOCKS);
  const [bookings, setBookings] = useState(SEED_BOOKINGS);

  // Booking state
  const [sel, setSel]           = useState(null);
  const [dur, setDur]           = useState(60);
  const [bookStep, setBookStep] = useState(1); // 1=details 2=bank 3=confirm 4=done
  const [form, setForm]         = useState({ name: "", email: "", phone: "", org: "", internal: false, code: "" });
  const [bankForm, setBankForm] = useState({ name: "", sort: "", account: "" });

  // Contact form
  const [contact, setContact]   = useState({ name: "", email: "", phone: "", msg: "" });
  const [contactSent, setContactSent] = useState(false);
  const ENQUIRY_EMAIL = "bookings@elliottcook.co.uk";

  // Admin
  const [adminAuth, setAdminAuth] = useState(false);
  const [pin, setPin]           = useState("");
  const [adminTab, setAdminTab] = useState("bookings");
  const [blockForm, setBlockForm] = useState({ day: "Mon", hour: 9, label: "", dur: 60, recurring: true });

  const [toast, setToast]       = useState(null);
  const weekDates = getWeekDates(weekOff);

  function showToast(msg, type = "ok") {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3400);
  }

  // Work out which hours a booking/block occupies
  function occupiedKeys(di, startH, durationMins) {
    const slots = durationMins / 60;
    return Array.from({ length: Math.max(1, slots) }, (_, i) => sKey(di, startH + i));
  }

  function slotStatus(di, h) {
    const k = sKey(di, h);
    // Check if this slot is part of a block that started earlier
    for (const [bk, bv] of Object.entries(blocked)) {
      const [bDay, bH] = bk.split("-");
      if (bDay !== DAYS[di]) continue;
      const slots = bv.dur / 60;
      for (let i = 0; i < slots; i++) {
        if (parseInt(bH) + i === h) return "blocked";
      }
    }
    for (const [bk, bv] of Object.entries(bookings)) {
      const [bDay, bH] = bk.split("-");
      if (bDay !== DAYS[di]) continue;
      const slots = Math.max(1, bv.dur / 60);
      for (let i = 0; i < slots; i++) {
        if (parseInt(bH) + i === h) return "booked";
      }
    }
    const d = new Date(weekDates[di]); d.setHours(h, 0, 0, 0);
    if (d < new Date()) return "past";
    return "available";
  }

  function canBook(di, h, durationMins) {
    const slots = Math.max(1, durationMins / 60);
    for (let i = 0; i < slots; i++) {
      if (slotStatus(di, h + i) !== "available") return false;
      if (h + i >= CLOSE_HOUR) return false;
    }
    return true;
  }

  function openBooking(di, h) {
    if (slotStatus(di, h) !== "available") return;
    setSel({ di, h, date: weekDates[di] });
    setDur(60);
    setBookStep(1);
    
    setForm({ name: "", email: "", phone: "", org: "", internal: false, code: "" });
    setBankForm({ name: "", sort: "", account: "" });
    
    setView("booking");
  }

  function submitDetails() {
    if (form.internal) {
      const newB = { type: "internal", name: form.name, email: form.email, dur, paid: true, method: "none" };
      setBookings(p => ({ ...p, [sKey(sel.di, sel.h)]: newB }));
      showToast("Booking confirmed — no charge for Dexters members!");
      setView("calendar");
    } else {
      setBookStep(2);
    }
  }

  function confirmPayment() {
    const newB = { type: "external", name: form.name, email: form.email, dur, paid: true, method: "bank" };
    setBookings(p => ({ ...p, [sKey(sel.di, sel.h)]: newB }));
    setBookStep(4);
    showToast("Booking confirmed!");
  }

  function addBlock() {
    if (!blockForm.label) return;
    const k = `${blockForm.day}-${blockForm.hour}`;
    setBlocked(p => ({ ...p, [k]: { label: blockForm.label, dur: blockForm.dur, recurring: blockForm.recurring } }));
    showToast("Block added");
    setBlockForm({ day: "Mon", hour: 9, label: "", dur: 60, recurring: true });
  }

  const price = PRICES[dur] || 45;
  const allBookings = Object.entries(bookings).map(([k, v]) => ({ k, ...v }));
  const allBlocks   = Object.entries(blocked).map(([k, v]) => ({ k, ...v }));

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text, fontFamily: "Inter, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&family=Inter:wght@400;500;600&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        input,select,textarea{outline:none;font-family:Inter,sans-serif}
        ::-webkit-scrollbar{width:5px}::-webkit-scrollbar-thumb{background:${C.faint};border-radius:3px}
        @keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
        @keyframes toastIn{from{opacity:0;transform:translateY(16px) scale(.95)}to{opacity:1;transform:translateY(0) scale(1)}}
        .avail{cursor:pointer;transition:all .12s}.avail:hover{filter:brightness(1.3);transform:scaleY(1.06)}
        .gold-btn:hover{background:#a00d24!important}
        .ghost-btn:hover{border-color:#c8102e!important;color:#c8102e!important}
        .nav-item:hover{color:#ffffff!important}
        .dur-opt:hover{border-color:#0d1f3c!important}
        .pay-opt:hover{border-color:#0d1f3c!important}
        .tab:hover{color:#f0f4f8!important}
      `}</style>

      {/* ════ HEADER ════ */}
      <header style={{ background: C.header, borderBottom: `3px solid ${C.gold}`, position: "sticky", top: 0, zIndex: 100, boxShadow: "0 2px 20px rgba(0,0,0,.5)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 20px", height: 62, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <a href="https://dextersportsyfc.org.uk" style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none" }}>

            <div>
              <div style={{ fontFamily: "Oswald", fontWeight: 900, fontSize: 19, letterSpacing: 3, color: "#ffffff", lineHeight: 1.1 }}>DEXTER SPORTS FC</div>
              <div style={{ fontFamily: "Oswald", fontSize: 10, letterSpacing: 3, color: "#f8c8d0", marginTop: 1 }}>3G PITCH BOOKING</div>
            </div>
          </a>
          <nav style={{ display: "flex", gap: 6 }}>
            {[["📅 Book", "calendar"], ["✉ Enquiry", "contact"], ["⚙ Admin", "admin"]].map(([lbl, v]) => (
              <button key={v} className="nav-item"
                onClick={() => v === "admin" ? (adminAuth ? setView("admin") : setView("adminLogin")) : setView(v)}
                style={{ fontFamily: "Oswald", fontWeight: 700, fontSize: 13, letterSpacing: 1, background: view === v ? C.gold : "transparent", border: `1px solid ${view === v ? C.gold : 'rgba(255,255,255,0.2)'}`, color: view === v ? "#fff" : "#a0b4cc", padding: "7px 14px", borderRadius: 6, cursor: "pointer", transition: "all .15s" }}>
                {lbl}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <main style={{ maxWidth: 1200, margin: "0 auto", padding: "30px 20px" }}>

        {/* ════ CALENDAR ════ */}
        {view === "calendar" && (
          <div style={{ animation: "fadeUp .3s ease" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 14 }}>
              <div>
                <h1 style={{ fontFamily: "Oswald", fontWeight: 900, fontSize: 36, letterSpacing: 2 }}>3G Pitch Availability</h1>
                <p style={{ color: C.muted, fontSize: 13, marginTop: 5 }}>
                  Learoyd Road, Canford Heath, Poole BH17 8PJ &nbsp;·&nbsp;
                  External hire: <b style={{ color: C.gold }}>from £{PRICES[30]}/30min</b>
                </p>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <button onClick={() => setWeekOff(w => w - 1)} style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.muted, width: 34, height: 34, borderRadius: 6, cursor: "pointer", fontSize: 18 }}>‹</button>
                <span style={{ fontFamily: "Oswald", fontSize: 14, letterSpacing: 1, color: C.muted, whiteSpace: "nowrap" }}>
                  {fmt(weekDates[0])} – {weekDates[6].toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                </span>
                <button onClick={() => setWeekOff(w => w + 1)} style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.muted, width: 34, height: 34, borderRadius: 6, cursor: "pointer", fontSize: 18 }}>›</button>
              </div>
            </div>

            {/* Pricing pills */}
            <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
              {Object.entries(PRICES).map(([d, p]) => (
                <div key={d} style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 20, padding: "5px 14px", fontSize: 12, color: C.muted }}>
                  <b style={{ color: C.gold }}>£{p}</b> · {DURATION_LABELS[d]}
                </div>
              ))}
              <div style={{ background: C.greenBg, border: `1px solid ${C.greenBdr}`, borderRadius: 20, padding: "5px 14px", fontSize: 12, color: C.muted }}>
                <b style={{ color: "#4ade80" }}>Free</b> · Dexters members
              </div>
            </div>

            {/* Legend */}
            <div style={{ display: "flex", gap: 18, marginBottom: 14, flexWrap: "wrap" }}>
              {[[C.green, "Available"], ["#0f2d1a", "Team Block"], [C.blue, "Booked"], [C.faint, "Past / Closed"]].map(([c, l]) => (
                <div key={l} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.muted }}>
                  <div style={{ width: 12, height: 12, borderRadius: 3, background: c, border: `1px solid ${c}` }} />
                  <span>{l}</span>
                </div>
              ))}
            </div>

            {/* Grid */}
            <div style={{ overflowX: "auto" }}>
              <div style={{ display: "grid", gridTemplateColumns: "48px repeat(7, 1fr)", gap: 3, minWidth: 580 }}>
                <div />
                {weekDates.map((d, i) => (
                  <div key={i} style={{ background: C.surface2, borderRadius: 4, padding: "7px 4px", textAlign: "center" }}>
                    <div style={{ fontFamily: "Oswald", fontWeight: 700, fontSize: 14, letterSpacing: 1, color: C.gold }}>{DAYS[i]}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{d.getDate()}/{d.getMonth() + 1}</div>
                  </div>
                ))}
                {HOURS.map(h => (
                  <>
                    <div key={`t${h}`} style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: 6, fontSize: 10, color: C.faint, fontFamily: "Oswald", letterSpacing: 1 }}>{h}:00</div>
                    {weekDates.map((_, di) => {
                      const s = slotStatus(di, h);
                      const k = sKey(di, h);
                      const bl = Object.entries(blocked).find(([bk]) => { const [bd, bh] = bk.split("-"); return bd === DAYS[di] && parseInt(bh) <= h && parseInt(bh) + (blocked[bk].dur / 60) > h; });
                      const bk2 = Object.entries(bookings).find(([bk]) => { const [bd, bh] = bk.split("-"); return bd === DAYS[di] && parseInt(bh) <= h && parseInt(bh) + Math.max(1, bookings[bk].dur / 60) > h; });
                      const isStart = bl && parseInt(bl[0].split("-")[1]) === h;
                      const isStart2 = bk2 && parseInt(bk2[0].split("-")[1]) === h;
                      const bg = s === "available" ? "#082812" : s === "blocked" ? "#051a0d" : s === "booked" ? "#071020" : "#0c1120";
                      const bdrL = s === "available" ? `3px solid ${C.green}` : s === "blocked" ? "3px solid #166534" : s === "booked" ? `3px solid ${C.blue}` : "none";
                      return (
                        <div key={`${di}${h}`} className={s === "available" ? "avail" : ""}
                          onClick={() => openBooking(di, h)}
                          title={s === "available" ? "Click to book" : bl ? bl[1].label : bk2 ? bk2[1].name : ""}
                          style={{ height: 34, borderRadius: 4, background: bg, borderLeft: bdrL, opacity: s === "past" ? 0.3 : 1, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                          {(isStart || isStart2) && (
                            <span style={{ fontSize: 9, color: "rgba(255,255,255,0.7)", fontFamily: "Oswald", letterSpacing: 0.4, padding: "0 3px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {isStart ? bl[1].label.split(" ").slice(-1)[0] : bk2[1].name.split(" ")[0]}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ════ BOOKING FLOW ════ */}
        {view === "booking" && sel && (
          <div style={{ maxWidth: 520, margin: "0 auto", animation: "fadeUp .3s ease" }}>
            <button onClick={() => setView("calendar")} style={{ background: "none", border: "none", color: "#c8102e", fontFamily: "Oswald", fontSize: 16, cursor: "pointer", letterSpacing: 1, marginBottom: 20 }}>← Back to calendar</button>

            {/* Progress bar */}
            {bookStep < 5 && (
              <div style={{ display: "flex", gap: 4, marginBottom: 22 }}>
                {["Details", "Payment", "Confirm"].map((lbl, i) => (
                  <div key={lbl} style={{ flex: 1 }}>
                    <div style={{ height: 3, borderRadius: 2, background: bookStep > i ? "#0d1f3c" : bookStep === i + 1 ? "#0d1f3c" : "#d0d8e8", opacity: bookStep === i + 1 ? 1 : bookStep > i + 1 ? 0.7 : 0.3, transition: "all .3s" }} />
                    <div style={{ fontSize: 10, color: bookStep >= i + 1 ? "#c8102e" : C.faint, textAlign: "center", marginTop: 4, fontFamily: "Oswald", letterSpacing: 1 }}>{lbl}</div>
                  </div>
                ))}
              </div>
            )}

            <div style={S.card}>
              {/* Slot badge */}
              <div style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 16px", marginBottom: 22, textAlign: "center" }}>
                <div style={{ fontFamily: "Oswald", fontWeight: 700, fontSize: 18, color: "#f0f4f8" }}>
                  {FULL_DAYS[sel.di]} {fmt(sel.date)} · {sel.h}:00 – {sel.h + (dur / 60)}:00
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>3G Pitch · Learoyd Road, Poole</div>
              </div>

              {/* STEP 1 — Details + duration */}
              {bookStep === 1 && (
                <>
                  <SectionHead>Your Details</SectionHead>

                  {/* Duration picker */}
                  <div style={{ marginBottom: 18 }}>
                    <label style={S.label}>Duration</label>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 6 }}>
                      {Object.entries(PRICES).map(([d, p]) => {
                        const ok = canBook(sel.di, sel.h, parseInt(d));
                        return (
                          <div key={d} className={ok ? "dur-opt" : ""}
                            onClick={() => ok && setDur(parseInt(d))}
                            style={{ border: `2px solid ${dur === parseInt(d) ? "#0d1f3c" : C.border}`, borderRadius: 8, padding: "10px 6px", textAlign: "center", cursor: ok ? "pointer" : "not-allowed", opacity: ok ? 1 : 0.35, transition: "border .15s", background: dur === parseInt(d) ? "#1a0a10" : "transparent" }}>
                            <div style={{ fontFamily: "Oswald", fontWeight: 700, fontSize: 15, color: dur === parseInt(d) ? "#c8102e" : C.text }}>{DURATION_LABELS[d]}</div>
                            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>£{p}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <Field label="Full Name" mt={0}><input style={S.inp} placeholder="e.g. John Smith" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></Field>
                  <Field label="Email"><input style={S.inp} placeholder="john@email.com" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} /></Field>
                  <Field label="Phone"><input style={S.inp} placeholder="+44 7700 000000" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} /></Field>
                  <Field label="Organisation / Team (optional)"><input style={S.inp} placeholder="e.g. Sunday Rovers FC" value={form.org} onChange={e => setForm(f => ({ ...f, org: e.target.value }))} /></Field>

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16, padding: "12px 14px", background: C.surface2, borderRadius: 8, border: `1px solid ${C.border}` }}>
                    <div>
                      <div style={{ fontFamily: "Oswald", fontWeight: 700, fontSize: 13, letterSpacing: 1 }}>DEXTER SPORTS MEMBER?</div>
                      <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>No charge with a valid team code</div>
                    </div>
                    <Toggle on={form.internal} onClick={() => setForm(f => ({ ...f, internal: !f.internal }))} />
                  </div>
                  {form.internal && <Field label="Team Access Code" mt={12}><input style={S.inp} placeholder="Enter your code" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} /></Field>}

                  {!form.internal && (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#0a1428", border: "1px solid #1e3a6b", borderRadius: 8, padding: "12px 16px", marginTop: 16 }}>
                      <div>
                        <div style={{ fontSize: 12, color: C.muted }}>Total to pay</div>
                        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Paid via GoCardless</div>
                      </div>
                      <span style={{ fontFamily: "Oswald", fontWeight: 900, fontSize: 28, color: "#c8102e" }}>£{price}</span>
                    </div>
                  )}

                  <button className="gold-btn" disabled={!form.name || !form.email} onClick={submitDetails}
                    style={{ ...S.goldBtn, opacity: (!form.name || !form.email) ? 0.4 : 1, cursor: (!form.name || !form.email) ? "not-allowed" : "pointer" }}>
                    {form.internal ? "Confirm Booking (Free)" : `Continue to Payment — £${price} →`}
                  </button>
                </>
              )}

              {/* STEP 2 — GoCardless Instant Bank Pay */}
              {bookStep === 2 && (
                <>
                  <InfoBanner icon="⚡" title="GoCardless — Instant Bank Pay" sub="One-off payment · Powered by Open Banking" />
                  <SectionHead>Bank Details</SectionHead>
                  <Field label="Account Holder Name" mt={0}><input style={S.inp} placeholder="As shown on your bank account" value={bankForm.name} onChange={e => setBankForm(f => ({ ...f, name: e.target.value }))} /></Field>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <Field label="Sort Code"><input style={S.inp} placeholder="00-00-00" maxLength={8} value={bankForm.sort} onChange={e => setBankForm(f => ({ ...f, sort: e.target.value }))} /></Field>
                    <Field label="Account Number"><input style={S.inp} placeholder="12345678" maxLength={8} value={bankForm.account} onChange={e => setBankForm(f => ({ ...f, account: e.target.value }))} /></Field>
                  </div>
                  <div style={{ background: "#0a1428", border: "1px solid #1e3a6b", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: C.muted, marginTop: 14 }}>
                    🔒 Your bank details are sent directly to GoCardless and never stored by Dexter Sports FC. This is a one-off payment — no recurring charges.
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#0a1428", border: "1px solid #1e3a6b", borderRadius: 8, padding: "12px 16px", marginTop: 12 }}>
                    <span style={{ color: C.muted, fontSize: 13 }}>Amount to pay</span>
                    <span style={{ fontFamily: "Oswald", fontWeight: 900, fontSize: 26, color: "#c8102e" }}>£{price}</span>
                  </div>
                  <button className="gold-btn"
                    disabled={!bankForm.name || !bankForm.sort || !bankForm.account}
                    onClick={() => setBookStep(3)}
                    style={{ ...S.goldBtn, opacity: (!bankForm.name || !bankForm.sort || !bankForm.account) ? 0.4 : 1, cursor: (!bankForm.name || !bankForm.sort || !bankForm.account) ? "not-allowed" : "pointer" }}>
                    Authorise Payment →
                  </button>
                  <button className="ghost-btn" onClick={() => setBookStep(1)} style={S.ghostBtn}>← Back</button>
                </>
              )}

              {/* STEP 3 — Confirmation summary */}
              {bookStep === 3 && (
                <>
                  <SectionHead>Confirm Your Booking</SectionHead>
                  <div style={{ background: C.surface2, borderRadius: 8, padding: "16px 18px", marginBottom: 18, fontSize: 13, lineHeight: 2.1, color: C.muted }}>
                    <div><b style={{ color: C.text }}>Name:</b> {form.name}</div>
                    <div><b style={{ color: C.text }}>Email:</b> {form.email}</div>
                    {form.org && <div><b style={{ color: C.text }}>Organisation:</b> {form.org}</div>}
                    <div><b style={{ color: C.text }}>Date:</b> {FULL_DAYS[sel.di]}, {fmt(sel.date)}</div>
                    <div><b style={{ color: C.text }}>Time:</b> {sel.h}:00 – {sel.h + dur / 60}:00 ({DURATION_LABELS[dur]})</div>
                    <div><b style={{ color: C.text }}>Venue:</b> 3G Pitch, Learoyd Road, Poole</div>
                    <div><b style={{ color: C.text }}>Amount:</b> <span style={{ color: "#c8102e", fontWeight: 700 }}>£{price}</span></div>
                    <div><b style={{ color: C.text }}>Method:</b> GoCardless Instant Bank Pay</div>
                  </div>
                  <button className="gold-btn" onClick={confirmPayment} style={S.goldBtn}>✓ Confirm &amp; Pay £{price}</button>
                  <button className="ghost-btn" onClick={() => setBookStep(2)} style={S.ghostBtn}>← Back</button>
                </>
              )}

              {/* STEP 4 — Done */}
              {bookStep === 4 && (
                <div style={{ textAlign: "center", padding: "24px 0" }}>
                  <div style={{ fontSize: 56 }}>✅</div>
                  <h2 style={{ fontFamily: "Oswald", fontWeight: 900, fontSize: 30, letterSpacing: 2, color: "#4ade80", marginTop: 14 }}>BOOKING CONFIRMED</h2>
                  <p style={{ color: C.muted, marginTop: 10, fontSize: 14 }}>A confirmation has been sent to <b style={{ color: C.text }}>{form.email}</b></p>
                  <p style={{ color: C.muted, fontSize: 12, marginTop: 6 }}>3G Pitch · Learoyd Road, Canford Heath, Poole BH17 8PJ</p>
                  <button className="gold-btn" onClick={() => setView("calendar")} style={{ ...S.goldBtn, marginTop: 24, width: "auto", padding: "11px 28px" }}>← Back to calendar</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ════ CONTACT / ENQUIRY ════ */}
        {view === "contact" && (
          <div style={{ maxWidth: 560, margin: "0 auto", animation: "fadeUp .3s ease" }}>
            <h1 style={{ fontFamily: "Oswald", fontWeight: 900, fontSize: 34, letterSpacing: 2, marginBottom: 6 }}>Pitch Enquiries</h1>
            <p style={{ color: C.muted, fontSize: 13, marginBottom: 24 }}>Questions about availability, group bookings, or anything else? Get in touch with the Dexters facilities team — or email us directly at <a href="mailto:bookings@elliottcook.co.uk" style={{ color: C.gold, textDecoration: "none" }}>bookings@elliottcook.co.uk</a></p>
            <div style={S.card}>
              {!contactSent ? (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <Field label="Your Name" mt={0}><input style={S.inp} placeholder="John Smith" value={contact.name} onChange={e => setContact(f => ({ ...f, name: e.target.value }))} /></Field>
                    <Field label="Email" mt={0}><input style={S.inp} placeholder="john@email.com" value={contact.email} onChange={e => setContact(f => ({ ...f, email: e.target.value }))} /></Field>
                  </div>
                  <Field label="Phone (optional)"><input style={S.inp} placeholder="+44 7700 000000" value={contact.phone} onChange={e => setContact(f => ({ ...f, phone: e.target.value }))} /></Field>
                  <Field label="Your Message">
                    <textarea style={{ ...S.inp, height: 130, resize: "vertical", lineHeight: 1.6 }}
                      placeholder="e.g. I'd like to enquire about hiring the 3G pitch for a tournament on 12th July for approx. 20 players..."
                      value={contact.msg} onChange={e => setContact(f => ({ ...f, msg: e.target.value }))} />
                  </Field>
                  <div style={{ display: "flex", gap: 10, alignItems: "center", background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", marginTop: 6 }}>
                    <span style={{ fontSize: 16 }}>📍</span>
                    <span style={{ fontSize: 12, color: C.muted }}>3G Pitch · Learoyd Road, Canford Heath, Poole BH17 8PJ &nbsp;·&nbsp; <a href="https://dextersportsyfc.org.uk" style={{ color: "#c8102e", textDecoration: "none" }}>dextersportsyfc.org.uk</a></span>
                  </div>
                  <button className="gold-btn"
                    disabled={!contact.name || !contact.email || !contact.msg}
                    onClick={() => {
                      if (!contact.name || !contact.email || !contact.msg) return;
                      // Open the user's mail client as a reliable fallback submission method
                      const subject = encodeURIComponent("3G Pitch Enquiry — Dexter Sports FC");
                      const body = encodeURIComponent(
                        `Name: ${contact.name}\nEmail: ${contact.email}\nPhone: ${contact.phone || "Not provided"}\n\nMessage:\n${contact.msg}`
                      );
                      window.open(`mailto:${ENQUIRY_EMAIL}?subject=${subject}&body=${body}`, "_blank");
                      setContactSent(true);
                    }}
                    style={{ ...S.goldBtn, opacity: (!contact.name || !contact.email || !contact.msg) ? 0.4 : 1, cursor: (!contact.name || !contact.email || !contact.msg) ? "not-allowed" : "pointer" }}>
                    Send Enquiry
                  </button>
                </>
              ) : (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                  <div style={{ fontSize: 48 }}>✉️</div>
                  <h2 style={{ fontFamily: "Oswald", fontWeight: 900, fontSize: 26, letterSpacing: 2, color: C.gold, marginTop: 14 }}>ENQUIRY SENT!</h2>
                  <p style={{ color: C.muted, marginTop: 10, fontSize: 14 }}>Thanks {contact.name}. The facilities team will be in touch at <b style={{ color: C.text }}>{contact.email}</b> shortly.</p>
                  <button className="gold-btn" onClick={() => { setContactSent(false); setContact({ name: "", email: "", phone: "", msg: "" }); }} style={{ ...S.goldBtn, width: "auto", padding: "10px 24px", marginTop: 22 }}>Send Another</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ════ ADMIN LOGIN ════ */}
        {view === "adminLogin" && (
          <div style={{ maxWidth: 360, margin: "60px auto", animation: "fadeUp .3s ease" }}>
            <div style={S.card}>
              <div style={{ textAlign: "center", marginBottom: 22 }}>
                <div style={{ fontSize: 38 }}>🔐</div>
                <h2 style={{ fontFamily: "Oswald", fontWeight: 900, fontSize: 26, letterSpacing: 2, marginTop: 10 }}>ADMIN ACCESS</h2>
                <p style={{ color: C.muted, fontSize: 13, marginTop: 4 }}>Dexter Sports FC — Facility Management</p>
              </div>
              <Field label="Admin PIN" mt={0}>
                <input style={S.inp} type="password" placeholder="••••" value={pin} onChange={e => setPin(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") { if (pin === "1234") { setAdminAuth(true); setView("admin"); setPin(""); } else showToast("Incorrect PIN", "err"); } }} />
              </Field>
              <button className="gold-btn" onClick={() => { if (pin === "1234") { setAdminAuth(true); setView("admin"); setPin(""); } else showToast("Incorrect PIN", "err"); }} style={S.goldBtn}>
                Enter Admin Panel
              </button>
              <p style={{ color: C.faint, fontSize: 11, textAlign: "center", marginTop: 12 }}>Demo PIN: 1234</p>
            </div>
          </div>
        )}

        {/* ════ ADMIN PANEL ════ */}
        {view === "admin" && adminAuth && (
          <div style={{ animation: "fadeUp .3s ease" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <div>
                <h1 style={{ fontFamily: "Oswald", fontWeight: 900, fontSize: 32, letterSpacing: 2 }}>ADMIN PANEL</h1>
                <p style={{ color: C.muted, fontSize: 13 }}>3G Pitch Management · Learoyd Road, Poole</p>
              </div>
              <button className="ghost-btn" onClick={() => { setAdminAuth(false); setView("calendar"); }} style={{ ...S.ghostBtn, width: "auto", padding: "7px 18px", marginTop: 0 }}>Log out</button>
            </div>

            {/* Stats */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(148px,1fr))", gap: 12, marginBottom: 26 }}>
              <Stat label="Total Bookings" value={allBookings.length}            color={C.gold}    />
              <Stat label="Paid"           value={allBookings.filter(b=>b.paid).length} color="#4ade80" />
              <Stat label="Unpaid"         value={allBookings.filter(b=>!b.paid).length} color="#f87171" />
              <Stat label="Team Blocks"    value={allBlocks.length}              color="#60a5fa"   />
              <Stat label="Revenue"        value={`£${allBookings.filter(b=>b.paid).reduce((acc,b)=>acc+(PRICES[b.dur]||45),0)}`} color={C.gold} />
            </div>

            {/* Tabs */}
            <div style={{ display: "flex", borderBottom: `1px solid ${C.border}`, marginBottom: 22 }}>
              {[["bookings","📋 Bookings"],["blocks","🚫 Team Blocks"],["add","➕ Add Block"]].map(([t,l]) => (
                <button key={t} className="tab" onClick={() => setAdminTab(t)}
                  style={{ fontFamily: "Oswald", fontWeight: 700, fontSize: 14, letterSpacing: 1, background: "none", border: "none", color: adminTab === t ? C.gold : C.muted, padding: "10px 22px", cursor: "pointer", borderBottom: adminTab === t ? `3px solid ${C.gold}` : "3px solid transparent", marginBottom: -1, transition: "color .15s" }}>
                  {l}
                </button>
              ))}
            </div>

            {/* Bookings table */}
            {adminTab === "bookings" && (
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "auto" }}>
                {allBookings.length === 0
                  ? <p style={{ color: C.faint, padding: 20 }}>No bookings yet.</p>
                  : (
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead><tr>{["Slot","Duration","Name","Email","Type","Method","Paid",""].map(h => <Th key={h}>{h}</Th>)}</tr></thead>
                      <tbody>
                        {allBookings.map(b => (
                          <tr key={b.k} style={{ borderBottom: `1px solid ${C.surface2}` }}>
                            <Td>{b.k.replace("-", " · ")}:00</Td>
                            <Td>{DURATION_LABELS[b.dur] || "1 hour"}</Td>
                            <Td>{b.name}</Td>
                            <Td style={{ color: C.muted }}>{b.email}</Td>
                            <Td><span style={{ background: b.type === "external" ? C.blueBg : C.greenBg, fontFamily: "Oswald", fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: "uppercase", padding: "3px 8px", borderRadius: 4 }}>{b.type}</span></Td>
                            <Td style={{ color: C.muted }}>{b.method === "bank" ? "Instant Pay" : b.method === "dd" ? "Direct Debit" : "—"}</Td>
                            <Td><span style={{ color: b.paid ? "#4ade80" : "#f87171", fontFamily: "Oswald", fontWeight: 700, fontSize: 12 }}>{b.paid ? "✓ PAID" : "✗ UNPAID"}</span></Td>
                            <Td><DangerBtn onClick={() => { const n = { ...bookings }; delete n[b.k]; setBookings(n); showToast("Booking cancelled"); }}>Cancel</DangerBtn></Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
              </div>
            )}

            {/* Blocks table */}
            {adminTab === "blocks" && (
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "auto" }}>
                {allBlocks.length === 0
                  ? <p style={{ color: C.faint, padding: 20 }}>No blocks set.</p>
                  : (
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead><tr>{["Slot","Duration","Team","Schedule",""].map(h => <Th key={h}>{h}</Th>)}</tr></thead>
                      <tbody>
                        {allBlocks.map(b => (
                          <tr key={b.k} style={{ borderBottom: `1px solid ${C.surface2}` }}>
                            <Td>{b.k.replace("-", " · ")}:00</Td>
                            <Td style={{ color: C.muted }}>{DURATION_LABELS[b.dur] || "1 hour"}</Td>
                            <Td>{b.label}</Td>
                            <Td><span style={{ color: b.recurring ? "#4ade80" : C.muted, fontFamily: "Oswald", fontWeight: 700, fontSize: 11, letterSpacing: 1 }}>{b.recurring ? "WEEKLY" : "ONE-OFF"}</span></Td>
                            <Td><DangerBtn onClick={() => { const n = { ...blocked }; delete n[b.k]; setBlocked(n); showToast("Block removed"); }}>Remove</DangerBtn></Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
              </div>
            )}

            {/* Add block form */}
            {adminTab === "add" && (
              <div style={{ maxWidth: 480 }}>
                <div style={S.card}>
                  <SectionHead>Add Team Block</SectionHead>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                    <Field label="Day" mt={0}><select style={S.inp} value={blockForm.day} onChange={e => setBlockForm(f => ({ ...f, day: e.target.value }))}>{DAYS.map(d => <option key={d}>{d}</option>)}</select></Field>
                    <Field label="Start Hour" mt={0}><select style={S.inp} value={blockForm.hour} onChange={e => setBlockForm(f => ({ ...f, hour: Number(e.target.value) }))}>{HOURS.map(h => <option key={h} value={h}>{h}:00</option>)}</select></Field>
                    <Field label="Duration" mt={0}><select style={S.inp} value={blockForm.dur} onChange={e => setBlockForm(f => ({ ...f, dur: Number(e.target.value) }))}>{Object.keys(PRICES).map(d => <option key={d} value={d}>{DURATION_LABELS[d]}</option>)}</select></Field>
                  </div>
                  <Field label="Team Name"><input style={S.inp} placeholder="e.g. Dexters U8" value={blockForm.label} onChange={e => setBlockForm(f => ({ ...f, label: e.target.value }))} /></Field>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16, padding: "10px 14px", background: C.surface2, borderRadius: 8 }}>
                    <div>
                      <div style={{ fontFamily: "Oswald", fontWeight: 700, fontSize: 13, letterSpacing: 1 }}>RECURRING WEEKLY?</div>
                      <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Repeats every week automatically</div>
                    </div>
                    <Toggle on={blockForm.recurring} onClick={() => setBlockForm(f => ({ ...f, recurring: !f.recurring }))} />
                  </div>
                  <button className="gold-btn" disabled={!blockForm.label} onClick={addBlock}
                    style={{ ...S.goldBtn, opacity: !blockForm.label ? 0.4 : 1, cursor: !blockForm.label ? "not-allowed" : "pointer" }}>
                    Add Block
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer style={{ borderTop: `1px solid ${C.border}`, padding: "18px 20px", marginTop: 48, textAlign: "center", background: "#0d1f3c" }}>
        <p style={{ color: "#8b9ab5", fontSize: 11, fontFamily: "Oswald", letterSpacing: 1 }}>
          DEXTER SPORTS FC CIC · FOUNDED 1975 · LEAROYD ROAD, CANFORD HEATH, POOLE BH17 8PJ
          &nbsp;·&nbsp;<a href="https://dextersportsyfc.org.uk" style={{ color: "#c8102e", textDecoration: "none" }}>dextersportsyfc.org.uk</a>
        </p>
      </footer>

      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", bottom: 24, right: 24, background: toast.type === "err" ? "#fde8ec" : "#dcfce7", border: `1px solid ${toast.type === "err" ? "#c8102e" : "#86efac"}`, color: "#0d1f3c", fontFamily: "Oswald", fontWeight: 700, fontSize: 14, letterSpacing: 1, padding: "11px 20px", borderRadius: 8, boxShadow: "0 4px 24px rgba(0,0,0,.6)", zIndex: 999, animation: "toastIn .3s ease" }}>
          {toast.type === "err" ? "✗" : "✓"} {toast.msg}
        </div>
      )}
    </div>
  );
}
